// Feature gates — check user permissions by plan
// Never use `user.plan === 'pro'` directly. Use these functions.

export type Plan = 'free' | 'pro' | 'unlimited' | 'enterprise'

export interface PlanLimits {
  aiRequestsPerDay: number
  storageMB: number
  maxProjects: number
  maxDeployments: number
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free:       { aiRequestsPerDay: 10,    storageMB: 500,    maxProjects: 3,   maxDeployments: 1 },
  pro:        { aiRequestsPerDay: 500,   storageMB: 5120,   maxProjects: -1,  maxDeployments: -1 },
  unlimited:  { aiRequestsPerDay: -1,    storageMB: 51200,  maxProjects: -1,  maxDeployments: -1 },
  enterprise: { aiRequestsPerDay: -1,    storageMB: -1,     maxProjects: -1,  maxDeployments: -1 },
}

export const PLAN_PRICES: Record<Plan, { monthly: number; label: string }> = {
  free:       { monthly: 0,  label: 'Free' },
  pro:        { monthly: 20, label: 'Pro' },
  unlimited:  { monthly: 49, label: 'Unlimited' },
  enterprise: { monthly: 99, label: 'Enterprise' },
}

// Feature access functions
export function canUseDashboard(_plan: Plan): boolean { return true }
export function canUseChat(_plan: Plan): boolean { return true }
export function canUsePreview(_plan: Plan): boolean { return true }
export function canUseTemplates(_plan: Plan): boolean { return true }

export function canUseCodeEditor(plan: Plan): boolean {
  return ['pro', 'unlimited', 'enterprise'].includes(plan)
}

export function canUseDeploy(plan: Plan): boolean {
  return ['pro', 'unlimited', 'enterprise'].includes(plan)
}

export function canUseCustomDomain(plan: Plan): boolean {
  return ['pro', 'unlimited', 'enterprise'].includes(plan)
}

export function canUseGitHub(plan: Plan): boolean {
  return ['pro', 'unlimited', 'enterprise'].includes(plan)
}

export function canUseIDE(plan: Plan): boolean {
  return ['unlimited', 'enterprise'].includes(plan)
}

export function canUseTerminal(plan: Plan): boolean {
  return ['unlimited', 'enterprise'].includes(plan)
}

export function canUseFileExplorer(plan: Plan): boolean {
  return ['unlimited', 'enterprise'].includes(plan)
}

export function canUsePackageInstall(plan: Plan): boolean {
  return ['unlimited', 'enterprise'].includes(plan)
}

export function canUseAdvancedDeploy(plan: Plan): boolean {
  return ['unlimited', 'enterprise'].includes(plan)
}

export function canUseSSO(plan: Plan): boolean {
  return plan === 'enterprise'
}

export function canUseAuditLogs(plan: Plan): boolean {
  return plan === 'enterprise'
}

export function canUseAdminDashboard(plan: Plan): boolean {
  return plan === 'enterprise'
}

export function canUseRBAC(plan: Plan): boolean {
  return plan === 'enterprise'
}

// Plan feature descriptions for UI
export const PLAN_FEATURES: Record<Plan, string[]> = {
  free: [
    'Dashboard + Chat + Preview',
    '10 AI requests/day',
    '500 MB storage',
    '3 active projects',
    '1 deployment',
    'Templates',
  ],
  pro: [
    'Everything in Free +',
    'Code Editor',
    '500 AI requests/day',
    '5 GB storage',
    'Unlimited projects',
    'Custom domains',
    'GitHub integration',
    'Multiple deployments',
  ],
  unlimited: [
    'Everything in Pro +',
    'Full IDE',
    'Terminal access',
    'File explorer',
    'Package install',
    'Unlimited AI',
    '50 GB storage',
    'Advanced deployments',
    'Background builds',
  ],
  enterprise: [
    'Everything in Unlimited +',
    'SSO (SAML/OIDC)',
    'Audit logs',
    'RBAC (roles & permissions)',
    'Admin dashboard',
    'Usage analytics',
    'Private AI agents',
    'Pay-as-you-go AI overage',
    'Priority support',
  ],
}

// Model access per plan — decoupled from model catalog
// Move models between plans without editing the model list
export const PLAN_MODEL_ACCESS: Record<Plan, string[]> = {
  free: [
    'auto',
    'gemini-2.5-flash',
    'mistral-medium-3.5',
    'openai/gpt-oss-120b:free',
    'openai/gpt-oss-20b:free',
    'meta-llama/llama-3.2-3b-instruct:free',
    'nvidia/nemotron-3-ultra-550b-a55b:free',
    'nvidia/nemotron-3-nano-omni-30b-a3b-reasoning:free',
    'nvidia/nemotron-3.5-content-safety:free',
    'pollinations-ai',
    'poolside/laguna-m.1:free',
    'poolside/laguna-xs.2:free',
    'openrouter/owl-alpha',
  ],
  pro: [
    'gemini-2.5-pro',
    'gpt-4o',
    'gpt-4o-mini',
    'claude-4-sonnet',
    'deepseek-chat',
  ],
  unlimited: [
    'claude-4-opus',
    'grok-3',
    'llama-4',
  ],
  enterprise: [
    // All models — enterprise gets everything
  ],
}

// Check if a user can access a specific model
export function canUseModel(plan: Plan, modelId: string): boolean {
  if (plan === 'enterprise') return true
  // Check current plan and all lower plans
  const levels: Plan[] = ['free', 'pro', 'unlimited']
  const userLevel = levels.indexOf(plan)
  for (let i = 0; i <= userLevel; i++) {
    if (PLAN_MODEL_ACCESS[levels[i]].includes(modelId)) return true
  }
  return false
}

// Get the minimum plan required for a model
export function getModelPlan(modelId: string): Plan {
  if (PLAN_MODEL_ACCESS.free.includes(modelId)) return 'free'
  if (PLAN_MODEL_ACCESS.pro.includes(modelId)) return 'pro'
  if (PLAN_MODEL_ACCESS.unlimited.includes(modelId)) return 'unlimited'
  return 'enterprise'
}
